import { useEffect, useRef, useState } from 'react';
import { AdCreative, detectDevice, serveAds } from '../../api/ads/ads.api';
import { IPostModel } from '../../models';
import { getHiddenIds } from '../../components/ads/native-ad-card';
import { REEL_AD_EVERY } from './reels-display-list';
import { adViewerKey } from '../../components/ads/ad-viewer-key';

const PREFETCH_SLOTS = 2;

/**
 * Lấy sẵn quảng cáo cho từng vị trí chèn (sau mỗi 6 reel) rồi mới cho chèn ô vào danh sách.
 * Serve rỗng/lỗi thì không có ô. Chỉ lấy cho vị trí chưa lướt qua, và bỏ kết quả đến muộn
 * khi người xem đã qua vị trí đó (tránh chèn phía trên làm lệch cuộn). Khách chưa đăng nhập cũng lấy, máy chủ quyết định.
 */
export const useReelAds = (
  posts: IPostModel[],
  activePostIndex: number,
  viewerId?: string
): Record<string, AdCreative> => {
  const [ads, setAds] = useState<Record<string, AdCreative>>({});
  const requested = useRef<Set<string>>(new Set());
  const activeRef = useRef(activePostIndex);
  activeRef.current = activePostIndex;
  const postsRef = useRef(posts);
  postsRef.current = posts;
  // Người xem máy chủ thấy (theo token). Trang vẽ lại khi hồ sơ đổi nên khoá được đọc lại đúng lúc.
  const viewerKey = adViewerKey();
  const viewerRef = useRef(viewerKey);

  // Đổi người xem (khách đăng nhập không tải lại trang): bỏ quảng cáo và vé của người xem cũ, lấy lại cho người xem mới.
  useEffect(() => {
    if (viewerRef.current === viewerKey) return;
    viewerRef.current = viewerKey;
    requested.current = new Set();
    setAds({});
  }, [viewerKey]);

  useEffect(() => {
    // Chỉ lấy trước 1-2 vị trí chèn sắp tới, không lấy hết các vị trí của trang.
    const upcoming = posts.filter((_, i) => (i + 1) % REEL_AD_EVERY === 0 && i >= activePostIndex).slice(0, PREFETCH_SLOTS);
    upcoming.forEach((post) => {
      if (requested.current.has(post.id)) return;
      if (viewerId && String((post as any).userId || '').toLowerCase() === viewerId.toLowerCase()) return;
      requested.current.add(post.id);
      const forViewer = viewerKey;
      serveAds({ device: detectDevice(), placement: 'Reels', postId: post.id, count: 1 })
        .then((res) => {
          if (!res?.length) return;
          if (viewerRef.current !== forViewer) return; // kết quả của người xem cũ đến muộn
          if (getHiddenIds().includes(res[0].campaignId)) return; // quảng cáo đã ẩn thì bỏ
          const idx = postsRef.current.findIndex((p) => p.id === post.id);
          if (idx < activeRef.current) return;
          setAds((prev) => ({ ...prev, [post.id]: res[0] }));
        })
        .catch(() => undefined);
    });
  }, [posts, activePostIndex, viewerId, viewerKey]);

  // Lượt vẽ ngay lúc đổi người xem: chưa kịp xoá, không trả quảng cáo của người xem cũ.
  return viewerRef.current === viewerKey ? ads : {};
};
